import { useState, useCallback } from "react";
import { UnionDayOfWeekType } from "src/pages/workOrder/workOrder-types";

// ==================== 타입 정의 ====================
type TimeSlot = {
  checked: boolean;
  minutes: number;
};

type DaySchedule = Record<string, TimeSlot>; // { "09:00": { checked: true, minutes: 540 } }
type WeekSchedule = Record<string, DaySchedule>; // { "MON": DaySchedule }

// ==================== 커스텀 훅 ====================
/**
 * 예약 스케줄 관리를 위한 커스텀 훅
 * 스케줄 상태와 관련 로직을 캡슐화
 */
const useReservationSchedule = (
  isEnabled: boolean,
  setIsDirtyState: (value: boolean) => void,
  createInitialSchedule: () => WeekSchedule,
  TIME_SLOTS: Array<{ time: string; minutes: number }>,
) => {
  const [timeSchedule, setTimeSchedule] = useState<WeekSchedule>(createInitialSchedule);
  // 개별 시간 슬롯 토글 - 자주 호출되므로 useCallback 유지
  const toggleTimeSlot = useCallback(
    (day: UnionDayOfWeekType, time: string) => {
      if (!isEnabled) return;

      setTimeSchedule((prev) => ({
        ...prev,
        [day]: {
          ...prev[day],
          [time]: {
            ...prev[day][time],
            checked: !prev[day][time].checked,
          },
        },
      }));
      setIsDirtyState(true);
    },
    [isEnabled, setIsDirtyState],
  );

  // 요일 전체 토글 - schedule 의존성 때문에 항상 재생성됨, 일반 함수로 변경
  const toggleAllDaySchedule = (day: string) => {
    if (!isEnabled) return;

    const daySchedule = timeSchedule[day];
    const allSelected = Object.values(daySchedule).every((slot) => slot.checked);

    setTimeSchedule((prev) => ({
      ...prev,
      [day]: TIME_SLOTS.reduce(
        (acc, { time, minutes }) => ({
          ...acc,
          [time]: {
            checked: !allSelected,
            minutes,
          },
        }),
        {},
      ),
    }));
    setIsDirtyState(true);
  };

  // 요일 전체 선택 여부 확인 - schedule 의존성 때문에 항상 재생성됨, 일반 함수로 변경
  const isDayFullySelected = (day: string) => {
    return Object.values(timeSchedule[day] || {}).every((slot) => slot.checked);
  };

  return {
    timeSchedule,
    setTimeSchedule,
    toggleTimeSlot,
    toggleAllDaySchedule,
    isDayFullySelected,
  };
};

export default useReservationSchedule;
export type { TimeSlot, DaySchedule, WeekSchedule };
